import { useEffect, useMemo, useState } from "react";
import { Target, TrendingUp, Award, CalendarDays } from "lucide-react";
import { api, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { bulanIniISO } from "@/lib/tanggal";

/** KPI Saya — tampil read-only untuk user yang punya employee_id linked.
 *  Endpoint /kpi-custom scope=own otomatis filter by employee_id di backend. */
export default function MyKpi() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [periode, setPeriode] = useState(() => bulanIniISO());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/kpi-custom?periode=${periode}`)
      .then(r => setItems(Array.isArray(r.data?.items) ? r.data.items : (r.data || [])))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [periode]);

  const summary = useMemo(() => {
    if (!items.length) return { total: 0, avg: 0, onTrack: 0 };
    let sumPct = 0, onTrack = 0;
    items.forEach(k => {
      const pct = (k.target || 0) > 0 ? Math.min(100, ((k.realisasi || 0) / k.target) * 100) : 0;
      sumPct += pct;
      if (pct >= 80) onTrack += 1;
    });
    return { total: items.length, avg: sumPct / items.length, onTrack };
  }, [items]);

  return (
    <div className="p-4 md:p-8 max-w-[1200px]" data-testid="my-kpi-page">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Portal Karyawan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Target className="w-6 h-6 md:w-7 md:h-7 text-emerald-600" /> KPI Saya
          </h1>
          <p className="mt-1.5 text-xs md:text-sm text-slate-500">
            Selamat datang, {user?.name}. Progress KPI kamu di periode {periode}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <MonthPicker value={periode} onChange={setPeriode}
            data-testid="my-kpi-periode" />
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Total KPI</p>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="my-kpi-total">{fmtNum(summary.total)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Rata-rata Pencapaian</p>
          <p className="font-display text-2xl font-bold text-emerald-700" data-testid="my-kpi-avg">{summary.avg.toFixed(1)}%</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">On Track (≥80%)</p>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="my-kpi-ontrack">
            {summary.onTrack} <span className="text-sm text-slate-400">/ {summary.total}</span>
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
          <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Detail KPI</h3>
        </div>
        <LoadingState
          loading={loading}
          kosong={!loading && items.length === 0}
          pesanKosong="Belum ada KPI untuk periode ini. Hubungi atasan untuk penetapan KPI bulan ini."
          data-testid="my-kpi-loading"
        >
          <div className="divide-y divide-slate-100">
            {items.map((k, i) => {
              const pct = (k.target || 0) > 0 ? Math.min(100, ((k.realisasi || 0) / k.target) * 100) : 0;
              const status = pct >= 100 ? "achieved" : pct >= 80 ? "ontrack" : pct >= 50 ? "warning" : "danger";
              const barColor = { achieved: "bg-emerald-500", ontrack: "bg-emerald-400", warning: "bg-amber-400", danger: "bg-rose-500" }[status];
              return (
                <div key={i} className="px-4 py-3.5" data-testid={`my-kpi-item-${i}`}>
                  <div className="flex items-start justify-between mb-1.5 gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 text-sm">{k.nama_kpi || k.name}</p>
                      <p className="text-[11px] text-slate-500">
                        Realisasi <span className="font-mono text-slate-700">{fmtNum(k.realisasi || 0)}</span> /
                        Target <span className="font-mono text-slate-700">{fmtNum(k.target || 0)}</span>
                        {k.unit ? <span className="ml-1 text-slate-400">{k.unit}</span> : null}
                      </p>
                    </div>
                    <div className={`text-xs font-bold px-2 py-1 rounded-full ${
                      status === "achieved" ? "bg-emerald-100 text-emerald-700"
                      : status === "ontrack" ? "bg-emerald-50 text-emerald-700"
                      : status === "warning" ? "bg-amber-100 text-amber-700"
                      : "bg-rose-100 text-rose-700"
                    }`}>
                      {pct.toFixed(1)}% {status === "achieved" && <Award className="inline w-3 h-3 ml-0.5" />}
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </LoadingState>
      </div>
    </div>
  );
}
